import { useDisclosure, DrawerContent, DrawerCloseButton } from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ControllableDrawer } from 'components/Drawer';
import { ConfirmationModal } from 'components/Modal';
import Table from 'components/Table';
import { useTableColumns } from 'containers/JobsTasks/JobsTabs/Active/useTableColumns';
import JobTrackMileageModal from 'containers/JobsTasks/Modal/TrackMileageModal';
import AssignEmployeesAndGroups from 'containers/JobsTasks/RightDrawer/AssignEmployeesAndGroupsForm';
import { initialAssignEmployeesAndGroupsData } from 'containers/JobsTasks/RightDrawer/AssignEmployeesAndGroupsForm/schema';
import FilterGroup from 'containers/JobsTasks/shared/FilterGroup';
// import { useConfigureGeofence } from 'hooks/jobs/useConfigureGeofence';
import { useDeactivateJobs } from 'hooks/jobs/useDeactivateJobs';
import { IDeleteJobsData, useDeleteJobs } from 'hooks/jobs/useDeleteJobs';
import { useFetchPaginatedJobs, IJobsSearchQuery } from 'hooks/jobs/useFetchPaginatedJobs';
import { useTrackMileageForJob } from 'hooks/jobs/useTrackMileageForJob';
import { useJobsTasksStore } from 'store/useJobsTasksStore';

import JobsTableFooter from './JobsTableFooter';

const initialJobsSearchQuery: IJobsSearchQuery = {
  page: 1,
  page_size: 25,
  job_status: '1',
  search: '',
  sort: '',
};

const Active: React.FC<unknown> = () => {
  const [drawerFormData, setDrawerFormData] = useState<typeof initialAssignEmployeesAndGroupsData>(
    initialAssignEmployeesAndGroupsData
  );
  const [dialogIds, setDialogIds] = useState<IDeleteJobsData>({ ids: [] });
  const [searchQuery, setSearchQuery] = useState<IJobsSearchQuery>(initialJobsSearchQuery);

  const { updateJobsTasksState, jobsTasksState, jobsTasksHasNotRedirected, jobsTasksRedirected, clearJobsTasksState } =
    useJobsTasksStore(state => state);

  const { t } = useTranslation(['jobsTasks', 'common']);
  const {
    isOpen: isDeactivateModalOpen,
    onOpen: onDeactivateModalOpen,
    onClose: onDeactivateModalClose,
  } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();
  const {
    isOpen: isTrackMileageModalOpen,
    onOpen: onTrackMileageModalOpen,
    onClose: onTrackMileageModalClose,
  } = useDisclosure();
  // const {
  //   isOpen: isRequireGeofencingModalOpen,
  //   onOpen: onRequireGeofencingModalOpen,
  //   onClose: onRequireGeofencingModalClose,
  // } = useDisclosure();

  const { status, data, refetch } = useFetchPaginatedJobs(searchQuery);
  const { status: deactivateJobsStatus, mutateAsync: mutateAsyncDeactivatedJobs } = useDeactivateJobs();
  const { status: deleteJobsStatus, mutateAsync: mutateAsyncDeletedJobs } = useDeleteJobs();
  const { status: trackMileageStatus, mutateAsync: mutateAsyncTrackMileage } = useTrackMileageForJob();
  // const { status: requireGeofenceStatus, mutateAsync: mutateAsyncConfigureGeofence } = useConfigureGeofence();

  // Caching the current page number and page size so that we know which page number the job's table was in before opening a new tab
  useEffect(() => {
    if (!jobsTasksRedirected) {
      if (searchQuery) {
        const newJobsTasksState: any = {
          ...jobsTasksState,
          current_page: searchQuery.page,
          per_page: searchQuery.page_size,
        };
        updateJobsTasksState(newJobsTasksState);
      }
    }

    jobsTasksHasNotRedirected();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    // Resets the values on page reload
    if (!jobsTasksRedirected) {
      window.addEventListener('beforeunload', () => clearJobsTasksState());

      return () => {
        window.removeEventListener('beforeunload', () => clearJobsTasksState());
      };
    }

    // Updating the searchQuery to match the redirect data
    setSearchQuery(prevState => ({
      ...prevState,
      page: jobsTasksState?.current_page,
      page_size: jobsTasksState?.per_page,
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSortChange = useCallback(
    (selectedSort: ILabelValueOptions) => {
      if (selectedSort) {
        setSearchQuery({ ...searchQuery, page: 1, sort: selectedSort.value });
      } else {
        setSearchQuery({ ...searchQuery, page: 1, sort: '' });
      }
    },
    [searchQuery]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();
      setSearchQuery({ ...searchQuery, page: 1, search: encodeURIComponent(searchValue) });
    }, 400),
    [searchQuery]
  );

  const handlePageSizeChange = (pageSize: number) => {
    setSearchQuery(queries => ({ ...queries, page_size: pageSize, page: 1 }));
  };

  // deactivate user handler
  const handleSingleDeactivate = (id: number) => {
    setDialogIds({ ids: [id] });
    onDeactivateModalOpen();
  };

  const handleBulkDeactivate = (selectedRows: any) => {
    const ids = selectedRows.map((row: any) => row.original.id);
    setDialogIds({ ids: ids });
    onDeactivateModalOpen();
  };

  const deactivateJobs = () => {
    mutateAsyncDeactivatedJobs(dialogIds).then(() => {
      refetch();
      onDeactivateModalClose();
      setDialogIds({ ids: [] });
    });
  };

  // delete user handler
  const handleSingleDelete = (id: number) => {
    setDialogIds({ ids: [id] });
    onDeleteModalOpen();
  };
  // delete user handler
  const handleBulkDelete = (selectedRows: any) => {
    const ids = selectedRows.map((row: any) => row.original.id);
    setDialogIds({ ids: ids });
    onDeleteModalOpen();
  };

  const deleteJobs = () => {
    mutateAsyncDeletedJobs(dialogIds).then(() => {
      refetch();
      onDeleteModalClose();
      setDialogIds({ ids: [] });
    });
  };

  // Track mileage
  const handleBulkTrackMileage = (selectedRows: any) => {
    const ids = selectedRows.map((row: any) => row.original.id);
    setDialogIds({ ids: ids });
    onTrackMileageModalOpen();
  };

  const handleBulkTrackMileageFormSubmit = (values: any) => {
    const postData = { ...dialogIds, track_mileage: values.trackMileage };
    mutateAsyncTrackMileage(postData).then(() => {
      refetch();
      onTrackMileageModalClose();
      setDialogIds({ ids: [] });
    });
  };

  // Require Geofencing
  // const handleBulkRequireGeofencing = (selectedRows: any) => {
  //   const ids = selectedRows.map((row: any) => row.original.id);
  //   setDialogIds({ ids: ids });
  //   onRequireGeofencingModalOpen();
  // };

  // const requireGeofencingForjobs = () => {
  //   const postData = { ...dialogIds, requireGeofence: 1 as 0 | 1 };
  //   mutateAsyncConfigureGeofence(postData).then(() => {
  //     refetch();
  //     onRequireGeofencingModalClose();
  //     setDialogIds({ ids: [] });
  //   });
  // };

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updateSearchQuery = (previousSearchQuery: IJobsSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updateSearchQuery);
  }, []);

  const handleDrawerOpen = (jobData: any) => {
    const drawerFormData = {
      ...initialAssignEmployeesAndGroupsData,
      id: jobData?.id,
      user_ids: jobData?.assigned_users || [],
    };
    setDrawerFormData(drawerFormData);
    onDrawerOpen();
  };

  const handleDrawerClose = () => {
    setDrawerFormData(initialAssignEmployeesAndGroupsData);
    onDrawerClose();
  };

  const { columns } = useTableColumns({ handleDrawerOpen, handleSingleDeactivate, handleSingleDelete });

  return (
    <div>
      <ControllableDrawer onClose={handleDrawerClose} isOpen={isDrawerOpen}>
        <DrawerContent>
          <DrawerCloseButton />
          <AssignEmployeesAndGroups onClose={handleDrawerClose} formData={drawerFormData} />
        </DrawerContent>
      </ControllableDrawer>

      <ConfirmationModal
        isOpen={isDeactivateModalOpen}
        onClose={onDeactivateModalClose}
        title={t('jobsTasks:deactivateJobModalTitle')}
        subTitle={t('jobsTasks:deactivateJobModalSubTitle')}
        confirmButtonLabel={t('common:deactivate')}
        confirmButtonProps={{
          bg: 'rgba(72,82, 175, 0.1)',
          color: 'primary.500',
          _hover: {
            bg: 'rgba(72,82, 175, 0.2)',
          },
          _active: {
            bg: 'rgba(72,82, 175, 0.2)',
          },
          isLoading: deactivateJobsStatus === 'loading',
          onClick: deactivateJobs,
        }}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        title={t('jobsTasks:deleteJobModalTitle')}
        subTitle={t('jobsTasks:deleteJobModalSubTitle')}
        confirmButtonLabel={t('common:delete')}
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          _active: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: deleteJobsStatus === 'loading',
          onClick: deleteJobs,
        }}
      />

      <JobTrackMileageModal
        onClose={onTrackMileageModalClose}
        isLoading={trackMileageStatus === 'loading'}
        isOpen={isTrackMileageModalOpen}
        handleFormSubmit={handleBulkTrackMileageFormSubmit}
      />

      {/* <ConfirmationModal
        isOpen={isRequireGeofencingModalOpen}
        onClose={onRequireGeofencingModalClose}
        title={t('jobsTasks:requireGeofencingModalTitle')}
        subTitle=""
        confirmButtonLabel={t('common:yes')}
        confirmButtonProps={{
          bg: 'rgba(72,82, 175, 0.1)',
          color: 'primary.500',
          _hover: {
            bg: 'rgba(72,82, 175, 0.2)',
          },
          _active: {
            bg: 'rgba(72,82, 175, 0.2)',
          },
          isLoading: requireGeofenceStatus === 'loading',
          onClick: requireGeofencingForjobs,
        }}
      /> */}

      <FilterGroup handleSortChange={handleSortChange} handleSearchInputChange={handleSearchInputChange} entity="Job" />

      <Table
        columns={columns}
        data={data?.payload || []}
        paginationData={data?.meta_data?.pagination}
        isLoading={status === 'loading'}
        enableRowSelect
        onPageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
        paginationStyle="rowsPerPage"
        emptyTableStateProps={{
          searchText: searchQuery.search,
        }}
        renderTableFooter={(selectedFlatRows: any, toggleAllRowSelected: any) => {
          return (
            <JobsTableFooter
              selectedFlatRows={selectedFlatRows}
              toggleAllRowSelected={toggleAllRowSelected}
              handleBulkDeactivate={handleBulkDeactivate}
              handleBulkDelete={handleBulkDelete}
              handleBulkTrackMileage={handleBulkTrackMileage}
            />
          );
        }}
      />
    </div>
  );
};

export default Active;
